import React, { forwardRef } from 'react';
import { Button, ButtonProps, useMediaQuery } from '@mui/material';
import {
  Add,
  ArrowBack,
  ArrowForward,
  Check,
  Delete,
} from '@mui/icons-material';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import { useForceBack } from 'src/hooks/useForceBack';
import { useNavigate } from 'react-router';

export type AddButtonProps = LoadingButtonProps & {
  adaptive?: boolean;
  noText?: boolean;
};
export const AddButton = forwardRef(
  (props: AddButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
    const { children, adaptive, noText, sx, ...rest } = props;
    const matches = useMediaQuery('(max-width: 600px)');
    const textHidden = noText || Boolean(adaptive && matches);

    return (
      <LoadingButton
        ref={ref}
        variant="contained"
        loadingPosition="start"
        startIcon={<Add />}
        sx={{ ...sx, ...(textHidden && { '.MuiButton-startIcon': { mx: 0 } }) }}
        {...rest}
      >
        {!textHidden && (children || 'Добавить')}
      </LoadingButton>
    );
  }
);

export const AddNewButton = forwardRef(
  (props: AddButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
    const navigate = useNavigate();

    return (
      <AddButton
        ref={ref}
        adaptive
        onClick={() => navigate('new')}
        {...props}
      />
    );
  }
);

export const DeleteButton = forwardRef(
  (
    {
      children,
      iconColor,
      ...rest
    }: LoadingButtonProps & { iconColor?: string },
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    return (
      <LoadingButton
        ref={ref}
        variant="outlined"
        color="accent"
        data-testid="delete-button"
        startIcon={<Delete sx={{ color: iconColor }} />}
        {...rest}
      >
        {children || 'Удалить'}
      </LoadingButton>
    );
  }
);

export const SubmitButton = forwardRef(
  (
    { children, ...rest }: LoadingButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    return (
      <LoadingButton
        ref={ref}
        type="submit"
        variant="contained"
        loadingPosition="start"
        startIcon={<Check />}
        data-testid="submit-button"
        {...rest}
      >
        {children || 'Сохранить'}
      </LoadingButton>
    );
  }
);

export const BackButton = forwardRef(
  (
    { children, backTo, ...rest }: ButtonProps & { backTo?: string },
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const { goBack, forceBack } = useForceBack(backTo);

    return (
      <Button
        ref={ref}
        color="primary"
        startIcon={<ArrowBack />}
        data-testid="back-button"
        {...(backTo && { onClick: () => goBack(forceBack) })}
        {...rest}
      >
        {children || 'Вернуться к списку'}
      </Button>
    );
  }
);

export const TransferButton = forwardRef(
  (
    { reverse, children, ...rest }: ButtonProps & { reverse?: boolean },
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    return (
      <Button
        ref={ref}
        color="secondary"
        variant="outlined"
        size="small"
        sx={{ minWidth: 'unset', width: '2.5rem', height: '2.4rem' }}
        {...rest}
      >
        {children || reverse ? (
          <ArrowBack fontSize="small" />
        ) : (
          <ArrowForward fontSize="small" />
        )}
      </Button>
    );
  }
);

export const GroupButton = forwardRef(
  (
    { children, isActive, ...rest }: ButtonProps & { isActive: boolean },
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    return (
      <Button
        ref={ref}
        color={isActive ? 'primary' : 'secondary'}
        variant={isActive ? 'contained' : 'outlined'}
        fullWidth
        {...rest}
      >
        {children}
      </Button>
    );
  }
);
